import * as ko from "knockout";
import {_} from 'lodash';
import {IFormTemplate, IFormTemplatePage, IObjectData, IRouteParams} from "../../../index";
import {copyToClipboard, decodeAndStripHtml} from "../../../util/misc";
import {global, lm} from "../../../appContext";
import {nsFa} from "../../fa/fn";
import {IRequestObject} from "../../../req/index";
import PromiseRequest from "../../../req/PromiseRequest";
import {getHash, getParentHash, getRouteDefinition, setRouteHash} from "../../../constants/RouteDefinitions";
import {memoryStore} from "../../../util/memorystore";
import {hasher} from 'hasher';

export {isFormTemplateV2, parseRouteParamVersion} from "./routeVersion";

const EHUB_RETURN_HASH_KEY = 'ehub.returnHash';

export namespace nsFormTemplate {

    import i = nsFa.i;

    export function getLink(formTemplate: IFormTemplate, isPreview: boolean) {
        return {
            icon: 'fa-form-template-eye',
            href: `/win.html#/formview/${formTemplate.companyid}/${formTemplate.id}/0${previewSegment(isPreview)}`,
            label: `${isPreview ? `Forhåndsvis` : `Vis`} skjema uten kontekst av bruker`,
        };
    }

    export function getCopyLink(formTemplate: IFormTemplate, isPreview: boolean) {
        return {
            icon: 'fa-link',
            onClick: () => copyToClipboard(`${document.location.origin}/win.html#/formview/${formTemplate.companyid}/${formTemplate.id}/0${previewSegment(isPreview)}`, 'URL til skjema'),
            label: `Kopier lenke til ${isPreview ? 'forhåndsvisning av' : ''} skjema`,
        };
    }

    function previewSegment(isPreview: boolean): string {
        return isPreview ? '/1' : '';
    }

    export function trashDialog(template: IFormTemplate, onConfirm: (reqObj: IRequestObject) => void) {
        const name = decodeAndStripHtml(template.name);
        const sentenceA = `message: you are about to delete subject|br|s:${name}`;
        const sentenceB = 'do you wish to continue'
        global.dialogManager.addConfirm({
            title: `${i('fa-trash-alt')} ((delete subject|s:((form template|ac))))`,
            content: lm.t(`<p>${lm.t(sentenceA)}</p><p>${lm.t(sentenceB)}</p>`) ?? '',
            buttons: {
                cancel: {
                    label: 'no, cancel',
                },
                confirm: {
                    cls: 'btn-danger',
                    onClick: () => {
                        const promise = new PromiseRequest('deleteformtemplate');
                        const request = promise
                            .request({id: template.id})
                            .then(() => {
                                global.cacheManager.removeCache('searchformtemplate');
                                global.cacheManager.removeCache('getformtemplate');
                                global.cacheManager.removeCache('getehubprocess');
                                global.snackbarData({message: 'Skjemamal er slettet.'});
                                setRouteHash('formtemplatesearch');
                            })
                        onConfirm({promise, request});
                    },
                    label: 'yes, continue',
                }
            }
        })
    }

    export function copyDialog(template: IFormTemplate, onConfirm: (reqObj: IRequestObject) => void, options?: {
        cb_newName?: (template: IFormTemplate) => string
    }) {
        const name = decodeAndStripHtml(template.name)
        const newName = decodeAndStripHtml(options?.cb_newName?.(template) ?? name) + ` (${lm.t('copy (duplicate)|ac')})`;
        const translationA = lm.t(`message: you are about to copy subject|s:${name}`)
        const translationB = lm.t(`new template name is set to inactive|name:${newName}`)
        const translationC = lm.t(`do you wish to continue`)
        global.dialogManager.addConfirm({
            title: `${i('fa-copy')} ((copy subject|s:((form template|ac))))`,
            content: `<p>${translationA} ${translationB}</p><p>${translationC}</p>`,
            buttons: {
                confirm: {
                    cls: 'btn-primary',
                    onClick: () => {
                        const data = _.cloneDeep(template);
                        data.name = newName
                        data.id = 0;
                        data.pages.forEach((p: IFormTemplatePage, i: number) => {
                            p.id = 0;
                            p.order = i + 1;
                        });
                        data.active = false;
                        const jformtemplate = encodeURIComponent(JSON.stringify(data));
                        const promise = new PromiseRequest('createformtemplate');
                        const request = promise
                            .request({jformtemplate: jformtemplate})
                            .then(() => {
                                global.cacheManager.removeCache('searchformtemplate');
                                global.cacheManager.removeCache('getehubprocess');
                                global.snackbarData({message: 'Meldingsmal er kopiert.'});
                            })
                        onConfirm({promise, request});
                    },
                    label: 'Ja, kopier',
                }
            }
        })
    }

    export function openFormDialog(form: IFormTemplate, obj?: IObjectData) {
        global.dialogManager.addComponent({
            title: `${i('fa-link')} ((open subject|s:((form|ac)))) [${form.name}]`,
            width: '50em',
            content: {
                name: "component/form/template/read/dialog/open",
                params: {form: form, obj: obj},
            }
        });
    }

    export function openFormDialog_v2(form: IFormTemplate, obj?: IObjectData) {
        const name = decodeAndStripHtml(form.name);
        global.dialogManager.addComponent({
            title: `${i('fa-file-export')} ((open subject|s:((form|ac)))) [${name}]`,
            width: '50em',
            isClosable: ko.observable(true),
            content: {
                name: "component/form/template/read/dialog/openv2",
                params: {form: form, obj: obj},
            }
        });
    }

    export function openV1form(item: IFormTemplate) {
        const p = {
            orgnr: global.login.selectedOrganizationId(),
            id: item.id,
            targetid: 0,
            preview: 0,
        }
        const hash = getHash('formview', p, true)
        window.open(`win.html${hash}`, '_blank');
        return false
    }

    export function openV2form(item?: IFormTemplate, params?: Partial<IRouteParams>) {
        memoryStore.get(EHUB_RETURN_HASH_KEY)(hasher.getHash());
        const p = getV2FormRouteParams(item, params)
        setRouteHash('formtemplatedetailsv', p)
    }

    export function navigateBackFromEhub(routeParams?: IRouteParams) {
        let returnHash = memoryStore.getVal(EHUB_RETURN_HASH_KEY);
        if (returnHash === null) {
            const routeDef = getRouteDefinition('formtemplatedetailsv');
            if (routeDef) {
                const parentHash = getParentHash(routeDef, routeParams ?? global.router.currentRoute()?.__routeParams, false);
                returnHash = Array.isArray(parentHash) ? parentHash[0] : parentHash;
            }
        }
        memoryStore.get(EHUB_RETURN_HASH_KEY)(null);
        if (returnHash) {
            hasher.replaceHash(returnHash);
        }
    }

    function getV2FormRouteParams(item?: IFormTemplate, params?: Partial<IRouteParams>): IRouteParams {
        const id = params?.['id'] ?? item?.['id'] ?? 0
        const mode = params?.['mode'] ?? 'edit'
        const version = params?.['version'] ?? ((item?.v2form ?? true) ? 2 : 1)
        return _.defaultsDeep(params ?? {}, {
            id,
            mode,
            version,
        })
    }

}